import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

// One-shot dictation for the Ask Bloom assistants, over two backends:
//
//   web    — the Web Speech API (Chrome/Safari desktop + mobile browsers)
//   native — @capacitor-community/speech-recognition
//
// The native path exists because the Android WebView that Capacitor runs the
// app in does NOT implement the Web Speech API (Chrome for Android does, the
// WebView doesn't). Detecting only `window.webkitSpeechRecognition` therefore
// left the mic button permanently disabled in the packaged app.
//
// Both backends are one-shot: listen until the speaker stops, then hand back a
// single final transcript. Callers get {supported, listening, toggle, stop}.

const WebSpeechRecognition =
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

const isNative = Capacitor.isNativePlatform();

// Neither backend can auto-detect the spoken language, so we dictate in the
// device's language; the backend then detects/translates the transcript to
// English (same path as typed text) before sending it to Claude.
const DICTATION_LOCALE =
    (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';

export function useDictation(onResult) {
    // On native, availability is an async device query (a device with no
    // recognition service installed reports false), so it starts unknown.
    const [supported, setSupported] = useState(isNative ? false : !!WebSpeechRecognition);
    const [listening, setListening] = useState(false);

    const recognitionRef = useRef(null);   // web: the live SpeechRecognition instance
    const listeningRef = useRef(false);    // read by stop() without re-creating the callback

    // Hold the latest callback in a ref so `toggle` stays referentially stable
    // even though callers pass a fresh closure each render.
    const onResultRef = useRef(onResult);
    useEffect(() => { onResultRef.current = onResult; }, [onResult]);

    const setListeningBoth = useCallback((v) => {
        listeningRef.current = v;
        setListening(v);
    }, []);

    useEffect(() => {
        if (!isNative) return undefined;
        let cancelled = false;
        SpeechRecognition.available()
            .then(({ available }) => { if (!cancelled) setSupported(!!available); })
            .catch(() => { if (!cancelled) setSupported(false); });
        return () => { cancelled = true; };
    }, []);

    const stop = useCallback(() => {
        if (!listeningRef.current) return;
        if (isNative) {
            SpeechRecognition.stop().catch(() => {});
            setListeningBoth(false);
        } else {
            recognitionRef.current?.stop();   // onend flips `listening` off
        }
    }, [setListeningBoth]);

    // Stop a live session if the component unmounts mid-utterance, otherwise
    // the recognizer keeps holding the mic.
    useEffect(() => () => {
        if (!listeningRef.current) return;
        if (isNative) SpeechRecognition.stop().catch(() => {});
        else recognitionRef.current?.stop();
    }, []);

    const startNative = useCallback(async () => {
        let state;
        try {
            ({ speechRecognition: state } = await SpeechRecognition.checkPermissions());
            if (state !== 'granted') {
                ({ speechRecognition: state } = await SpeechRecognition.requestPermissions());
            }
        } catch {
            setListeningBoth(false);
            return;
        }
        // Denied at the OS prompt — leave the button idle rather than opening a
        // recognizer that would immediately error.
        if (state !== 'granted') { setListeningBoth(false); return; }

        setListeningBoth(true);
        try {
            // popup:false keeps dictation inline (the system dialog would cover
            // the thread and swallow partialResults); maxResults:1 mirrors the
            // web path's maxAlternatives=1.
            const { matches } = await SpeechRecognition.start({
                language: DICTATION_LOCALE,
                maxResults: 1,
                partialResults: false,
                popup: false,
            });
            const transcript = matches?.[0];
            if (transcript) onResultRef.current?.(transcript);
        } catch {
            // no-speech / cancelled / recognizer busy — fall through to idle
        } finally {
            setListeningBoth(false);
        }
    }, [setListeningBoth]);

    const startWeb = useCallback(() => {
        const rec = new WebSpeechRecognition();
        rec.lang = DICTATION_LOCALE;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            if (transcript) onResultRef.current?.(transcript);
        };
        rec.onend = () => setListeningBoth(false);
        rec.onerror = () => setListeningBoth(false);
        recognitionRef.current = rec;
        setListeningBoth(true);
        rec.start();
    }, [setListeningBoth]);

    const toggle = useCallback(() => {
        if (!supported) return;
        if (listeningRef.current) { stop(); return; }
        if (isNative) startNative();
        else startWeb();
    }, [supported, stop, startNative, startWeb]);

    return { supported, listening, toggle, stop };
}
